'use client';

import CustomButton from '@/components/custom-button';
import CustomInput from '@/components/custom-input';
import CustomForm, { FormItemProps } from '@/components/custom-RHF-form';
import { template_error } from '@/components/custom-toast/sonner-with-template';
import { cn } from '@/lib/utils';
import {
    forgotPassword,
    resetPasswordWithToken,
} from '@/server/api/v1/auth/auth.action';
import { ChangePasswordWithTokenRequest } from '@/server/api/v1/user/type';
import { ResetCodeType } from '@/types/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import CustomOTP from '../custom-otp';
import CustomToast from '../custom-toast';
import { RePassword, RePasswordSchema } from './schema';

const defaultValues: RePassword = {
    old: '_default_',
    new: '',
    confirm_new: '',
};

type CustomRePasswordProps = {
    className?: string;

    username: string;
    email?: string;

    /**callback change password sucess */
    onSuccess: () => void | Promise<void>;
    /**callback change password failed */
    onError: (
        errors: string[],
        /**
         * Nếu token được sử dụng và bị lỗi
         * thì lại load lại token mớimới
         */
        isReloadToken?: boolean
    ) => void | Promise<void>;

    alert?: ReactNode;
    onClearAlert: VoidFunction;
};

const { sonner } = CustomToast;

const CustomRePasswordWithOTP = ({
    className,
    username,
    email,
    alert,
    onClearAlert,
    onSuccess,
    onError,
}: CustomRePasswordProps) => {
    const transDef = useTranslations('System');
    const transRePwd = useTranslations('RePassword');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [otp, setOtp] = useState('');

    const methods = useForm<RePassword>({
        mode: 'onBlur',
        defaultValues: defaultValues,
        delayError: 500,
        resolver: zodResolver(RePasswordSchema(transDef)),
    });

    /**
     * Gửi lại OTP
     */
    const callbackSendOTP = useCallback(async () => {
        if (username) {
            const result = await forgotPassword({
                userName: username,
                type: ResetCodeType.OTP,
            });
            if (!result?.data?.isSuccessStatus) {
                console.error(result?.data?.errors);
                sonner.error(
                    template_error({
                        title: 'Đã có lỗi xảy ra',
                        desc: result?.data?.errors,
                    })
                );
            }
            onClearAlert();
        }
    }, [onClearAlert, username]);

    const onChangePassword = useCallback(
        async (data: RePassword) => {
            // Nếu có truyền token thì gọi api đổi mật khẩu bằng token
            if (!!username) {
                onClearAlert();

                const sendData: ChangePasswordWithTokenRequest = {
                    UserName: username,
                    ResetCode: otp,
                    NewPassword: data.new,
                    ResetCodeType: ResetCodeType.OTP,
                };

                if (!!email) {
                    sendData.Email = email;
                }

                setIsSubmitting(true);

                const result = await resetPasswordWithToken(sendData);

                if (result?.data?.isSuccessStatus) {
                    onSuccess();
                } else {
                    console.log({ result });
                    await onError(result?.data?.errors ?? [], true);
                    setIsSubmitting(false);
                }

                methods.reset(defaultValues);
            } else {
                onError(['Thông tin không hợp lệ!']);
            }

            setIsSubmitting(false);
        },
        [email, methods, onClearAlert, onError, onSuccess, otp, username]
    );

    const rls = useMemo<FormItemProps<RePassword>>(() => {
        const subRls: FormItemProps<RePassword> = [
            {
                type: 'Item',
                name: 'new',
                label: transRePwd('NewPassword'),
                required: true,
                wrapperClassName: 'col-span-full',
                children: (
                    <CustomInput.Password
                        className='no-ring shadow-b-sm'
                        placeholder={transDef('System_Enter') + '...'}
                        autoComplete='new-password'
                        readOnly={isSubmitting}
                    />
                ),
            },
            {
                type: 'Item',
                name: 'confirm_new',
                label: transRePwd('ConfirmPassword'),
                required: true,
                wrapperClassName: 'col-span-full',
                children: (
                    <CustomInput.Password
                        className='no-ring shadow-b-sm'
                        placeholder={transDef('System_Enter') + '...'}
                        autoComplete='new-password'
                        readOnly={isSubmitting}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                methods.handleSubmit(
                                    onChangePassword,
                                    (err) => {
                                        console.error(
                                            `change-password-form-failed`,
                                            err
                                        );
                                    }
                                )();
                            }
                        }}
                    />
                ),
            },
        ];

        return [
            {
                type: 'Grid',
                children: subRls,
            },
        ];
    }, [isSubmitting, methods, onChangePassword, transDef, transRePwd]);

    return (
        <CustomForm.FormProvider {...methods}>
            <div className={cn('max-w-xl', className)}>
                <CustomForm.Form rls={rls}>
                    {alert && <div className='mb-2'>{alert}</div>}

                    <CustomOTP.Root
                        callbackReSendOTP={callbackSendOTP}
                        value={otp}
                        onChange={setOtp}
                        range={6}
                    />

                    <div className='flex items-center justify-center gap-x-2 gap-y-1 pt-3'>
                        <CustomButton.Root
                            className={cn('min-w-32 max-md:flex-1')}
                            icon={<KeyRound size={16} />}
                            onClick={methods.handleSubmit(
                                onChangePassword,
                                (err) => {
                                    console.error(
                                        `change-password-form-failed`,
                                        err
                                    );
                                }
                            )}
                            isLoading={isSubmitting}
                            disabled={otp.length !== 6}
                        >
                            {transRePwd('ChangePassword')}
                        </CustomButton.Root>
                    </div>
                </CustomForm.Form>
            </div>
        </CustomForm.FormProvider>
    );
};

export default memo(CustomRePasswordWithOTP);
